import { ServiceError } from "@exceptions"
import type { LoginResponse, GetUserResponse } from "@services/auth.service"
import type { SearchAddressResponse } from "@services/address.service"
import type { SearchPdlResponse, SearchPoint } from "@services/enedis.service"
import type { CreateCSTBRecordDto } from "@services/wattwatchers.service"
import { Model } from "@types"

// Import tous les types nécessaires pour les services.
/**
 * Type définissant les appels aux services externes de l'application.
 *
 * Le format est le suivant : ${nom du service}: (paramètres) => Promise<type de retour>
 *
 *
 */
// TODO : Ajouter les services externes de l'application.
type Services = {
    /**
     * Balise de progression du formulaire (DEV-1061) — fire-and-forget.
     * @param _id_cstb_record - Identifiant du dossier
     * @param step - Étape courante de la machine
     */
    $postFormProgress(_id_cstb_record: string, step: string): Promise<void>
    /**
     * Connexion à l'API métier
     * @param username - Email d'utilisateur
     * @param password - Mot de passe
     */
    $login(username: string, password: string): Promise<LoginResponse>
    /**
     * Récupère l'utilisateur connecté
     */
    $getUser: Promise<GetUserResponse>
    /**
     * Récupère un CSTBRecord par son identifiant
     * @param id - Identifiant du CSTBRecord
     */
    $getCSTBRecordById(
        id: string
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]>
    /**
     * Recherche des adresses par recherche textuelle
     * @param text - Texte de recherche
     */
    $searchAddress(text: string): Promise<ServiceResponse["GetAddressResponse"]>
    /**
     * Recherche des points de livraison pour un particulier
     * @param params - Paramètres de recherche
     * @returns Le point de livraison trouvé
     */
    $searchPoint(
        params: SearchPoint
    ): Promise<ServiceResponse["GetSearchPointResponse"]>

    /**
     * Recherche un point de livraison pour un bâtiment
     * @param _id_building
     * @param _id_cstb_record
     */
    $searchPdlForBuilding(
        _id_building: string,
        _id_cstb_record: string
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]>

    /**
     * Crée un dossier CSTB
     * @param data
     */
    $createCSTBRecord(
        data: CreateCSTBRecordDto
    ): Promise<ServiceResponse["PostCSTBRecordResponse"]>

    /**
     * Permet de signer le consentement d'une source d'un bâtiment d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param idtype - Le type de la source
     * @param idsource - L'ID réel de la source
     * @param options - Options supplementaires
     * @param [options.consent_checked] - `true` quand la case a été cochée (DEV-2274)
     * @returns Le dossier CSTB mis à jour
     */
    $signSource(
        _id_cstb_record: string,
        _id_building: string,
        idtype: string,
        idsource: string,
        options?: {
            consent_checked?: boolean
        }
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]>

    /**
     * Ajoute une source à un bâtiment d'un dossier CSTB sans la signer
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param idtype - Le type de la source
     * @param idsource - L'ID réel de la source
     * @param date_from_data - Date de début du contrat (pour PCE uniquement)
     * @returns Le dossier CSTB mis à jour
     */
    $addSource(
        _id_cstb_record: string,
        _id_building: string,
        idtype: string,
        idsource: string,
        date_from_data?: string
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]>

    /**
     * Permet de supprimer une source d'un bâtiment d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param idtype - Le type de la source (pdl ou pce)
     * @param idsource - L'ID réel de la source
     */
    $deleteSource(
        _id_cstb_record: string,
        _id_building: string,
        idtype: string,
        idsource: string
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]>

    /**
     * Met à jour les informations utilisateur d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param payload - Les données utilisateur à mettre à jour
     * @param payload.firstname - Le prénom de l'utilisateur
     * @param payload.lastname - Le nom de l'utilisateur
     * @param payload.phone - Le numéro de téléphone de l'utilisateur
     * @returns Le dossier CSTB mis à jour
     */
    $updateCSTBRecordUser(
        _id_cstb_record: string,
        payload: Partial<
            Record<"firstname" | "lastname" | "username" | "phone", string>
        >
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]>

    /**
     * Met à jour les informations d'un bâtiment d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param payload - Les données à mettre à jour
     * @param payload.name - Le nom du bâtiment
     * @param payload.address - L'adresse du bâtiment
     * @param payload.info_logement - Les informations sur le logement
     * @returns Le dossier CSTB mis à jour
     */
    $patchBuilding(
        _id_cstb_record: string,
        _id_building: string,
        payload: Partial<{
            name: string
            address: object
            info_logement: any
        }>
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]>

    /**
     * Signe le consentement d'un bâtiment d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     */
    $signFostAndDpeConsent(
        _id_cstb_record: string,
        _id_building: string,
        options: { text: string; consent_checked?: boolean }
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]>

    /**
     * Envoie un mail de fin quand le dossier CSTB est terminé
     * @param _id_cstb_record - L'ID du dossier CSTB
     */
    $sendFinalMail(_id_cstb_record: string): Promise<void>

    /**
     * Désabonne un utilisateur des mails
     * @param username - L'email de l'utilisateur
     */
    $unsubscribeMails(username: string): Promise<void>
}

/**
 * Regroupe tous les types de l'application concernant les réponses aux services.
 */
type ServiceResponse = {
    LoginResponse: LoginResponse
    GetUserResponse: GetUserResponse
    GetCSTBRecordByIdResponse: Model["CSTBRecord"]
    PostCSTBRecordResponse: Model["CSTBRecord"]
    GetAddressResponse: SearchAddressResponse[]
    GetSearchPointResponse: SearchPdlResponse
}

/**
 * Fonction permettant de gérer la logique d'appel d'une API et de gérer les erreurs qui peuvent survenir.
 * Si une erreur survient, elle est transformée en `ServiceError` pour être traitée plus facilement dans les composants.
 *
 * Ceci est un exemple d'utilisation lors de l'appel d'une API :
 * ```typescript
 * handleServiceCall(async () => {
 *    const { data } = await axios.get("/api/gateway/auth/widget/verify-key", {
 *       params: {
 *         type: "data",
 *      },
 *     headers: {
 *      "x-widget-key": key,
 *    },
 * })
 * ```
 *
 * @param serviceCall - Fonction d'appel de l'API
 * @returns
 */
async function handleServiceCall<T>(serviceCall: () => Promise<T>): Promise<T> {
    return serviceCall().catch((error) => {
        if (error.response) {
            // L'API métier sérialise ses erreurs en `{ detail: { message, code,
            // context } }` (api-ww `filters/http-exception.filter.ts`). Le
            // `context` porte le discriminant métier — sur la création d'un
            // dossier, le code est `error-ajout` pour tout refus et seul
            // `context.message` dit qu'il s'agit d'un doublon d'email
            // (DEV-1660). Lecture défensive : une réponse sans `detail` (proxy,
            // 502/504) ne doit pas devenir un TypeError qui masque le vrai
            // statut HTTP.
            const detail = error.response.data?.detail
            throw new ServiceError(
                {
                    message:
                        detail?.message ?? "Une erreur inconnue est survenue",
                    code: detail?.code ?? "unknown-error",
                    description: error.message,
                    context: detail?.context,
                },
                error.response.status
            )
        } else {
            throw new ServiceError(
                {
                    message: "Une erreur inconnue est survenue",
                    code: "UNKNOWN",
                    description: error.message,
                },
                500
            )
        }
    })
}

export type { Services, ServiceResponse }
export { handleServiceCall }
